import React, { useState, useEffect } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useEmployee, useDirectReports } from '@/hooks/useWorkforce'
import { useUpdateEmployeeStatus, useReassignManager } from '@/hooks/useWorkforceMutations'
import { useSalaryAccess } from '@/hooks/useSalaryAccess'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmployeeDetailSkeleton } from '@/components/common/EmployeeDetailSkeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { UpdateEmployeeStatusModal } from '@/components/workforce/UpdateEmployeeStatusModal'
import { ReassignManagerModal } from '@/components/workforce/ReassignManagerModal'
import { EmployeePictureUpload } from '@/components/workforce/EmployeePictureUpload'
import { EmployeeAvatar } from '@/components/vacation/EmployeeAvatar'
import { SensitiveText, SensitiveValue } from '@/components/privacy/SensitiveText'
import { getEmployee } from '@/api/workforce'
import type { Employee } from '@/types/workforce'
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Building2,
  Briefcase,
  User,
  Users,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  MapPin,
  FileText,
  DollarSign,
  RefreshCw
} from 'lucide-react'

function EmployeeDetailsContent() {
  const { employeeId } = useParams({ from: '/_auth/workforce/employees/$employeeId/' })
  const { employee, loading, error } = useEmployee(employeeId)
  const { reports, loading: reportsLoading } = useDirectReports(employeeId, { limit: 100 })
  const { canViewSalaries, formatSalaryValue } = useSalaryAccess()
  const { user } = useAuth()
  const updateStatusMutation = useUpdateEmployeeStatus()
  const reassignManagerMutation = useReassignManager()
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)

  const tenantDomain = user?.employee?.tenantId || "default"

  // No need to load currentManager - we have managerName/managerEmail at root level!

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-success" />
      case 'INACTIVE':
        return <XCircle className="w-5 h-5 text-brand-gray" />
      case 'PENDING':
        return <Clock className="w-5 h-5 text-warning" />
      case 'TERMINATED':
        return <AlertTriangle className="w-5 h-5 text-error" />
      default:
        return <Clock className="w-5 h-5 text-brand-gray" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'PENDING':
        return 'bg-warning/20 text-warning'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A'
    try {
      const dateObj = new Date(date)
      if (isNaN(dateObj.getTime())) return 'N/A'
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dateObj)
    } catch {
      return 'N/A'
    }
  }

  const handleReassignManager = async (newManagerId: string | null, newManagerName?: string | null, newManagerEmail?: string | null) => {
    if (!employee) return

    try {
      await reassignManagerMutation.mutateAsync({
        employeeId: employee.employeeId,
        newManagerId,
        newManagerName: newManagerName ?? null,
        newManagerEmail: newManagerEmail ?? null
      })
      setShowReassignModal(false)
    } catch (error) {
      console.error('Error reassigning manager:', error)
    }
  }

  if (loading) {
    return <EmployeeDetailSkeleton />
  }

  if (error || !employee) {
    return (
      <EmptyState
        icon={User}
        title="Colaborador não encontrado"
        description={error || 'Não foi possível carregar os dados do colaborador.'}
        action={
          <Link to={"/workforce/employees" as any}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para Lista
            </Button>
          </Link>
        }
      />
    )
  }

  const name = employee.personalInfo?.name || 'Nome não informado'
  const nameParts = name.split(' ').filter(p => p.length > 0)
  const initials = nameParts.length >= 2 ?
    `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` :
    nameParts.length === 1 ?
    `${nameParts[0][0]}${nameParts[0][1] || 'N'}` :
    'NA'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={"/workforce/employees" as any}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
              {name}
            </h1>
            <p className="font-body text-brand-gray mt-1">
              <SensitiveText type="email">{employee.email}</SensitiveText>
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStatusModal(true)}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Alterar Status
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowReassignModal(true)}
          >
            <Users className="w-4 h-4 mr-2" />
            Atribuir Gestor
          </Button>
          <Link to={`/workforce/employees/${employeeId}/edit` as any}>
            <Button size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>
      </div>

      {/* Profile Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <EmployeePictureUpload
              employeeId={employeeId}
              currentPictureUrl={employee.personalInfo?.pictureUrl}
              employeeName={name}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-2xl font-semibold text-white">
                {name}
              </h2>
              <Badge className={getStatusColor(employee.status)}>
                <div className="flex items-center gap-1.5">
                  {getStatusIcon(employee.status)}
                  <span className="capitalize">{employee.status?.toLowerCase()}</span>
                </div>
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-brand-gray">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>
                  <SensitiveText type="email">{employee.email || 'N/A'}</SensitiveText>
                </span>
              </div>
              {employee.personalInfo?.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>
                    <SensitiveText type="phone">{employee.personalInfo.phone}</SensitiveText>
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{employee.currentEngagement?.departmentName || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                <span>{employee.currentEngagement?.roleName || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Admissão: {formatDate(employee.currentEngagement?.startDate)}</span>
              </div>
              {canViewSalaries && employee.currentEngagement?.salary && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span>
                    <SensitiveValue mask="R$ ••••••">
                      {formatSalaryValue(employee.currentEngagement.salary)}
                    </SensitiveValue>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Employment Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Informações de Emprego
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-brand-gray">Status:</span>
              <span className="text-white font-medium capitalize">
                {employee.status?.toLowerCase() || 'N/A'}
              </span>
            </div>
            {employee.managerId && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Gestor:</span>
                <Link
                  to={`/workforce/employees/${employee.managerId}` as any}
                  className="text-brand-primary hover:text-brand-primary-dark font-medium transition-colors"
                >
                  {employee.managerName || 'Ver Gestor'}
                </Link>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-brand-gray">Data de Admissão:</span>
              <span className="text-white font-medium">
                {formatDate(employee.currentEngagement?.startDate)}
              </span>
            </div>
            {employee.currentEngagement?.endDate && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Data de Saída:</span>
                <span className="text-white font-medium">
                  {formatDate(employee.currentEngagement.endDate)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-brand-gray">Tipo de Contrato:</span>
              <span className="text-white font-medium">
                {employee.currentEngagement?.contractType || 'N/A'}
              </span>
            </div>
            {canViewSalaries && (
              <>
                <div className="flex justify-between">
                  <span className="text-brand-gray">Salário:</span>
                  <span className="text-white font-medium">
                    <SensitiveValue mask="R$ ••••••">
                      {formatSalaryValue(employee.currentEngagement?.salary ?? undefined)}
                    </SensitiveValue>
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Informações Pessoais
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-brand-gray">Nome Completo:</span>
              <span className="text-white font-medium">
                {employee.personalInfo?.name || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Email:</span>
              <span className="text-white font-medium">
                <SensitiveText type="email">{employee.email}</SensitiveText>
              </span>
            </div>
            {employee.personalInfo?.phone && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Telefone:</span>
                <span className="text-white font-medium">
                  <SensitiveText type="phone">{employee.personalInfo.phone}</SensitiveText>
                </span>
              </div>
            )}
            {employee.personalInfo?.address && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Endereço:</span>
                <span className="text-white font-medium">
                  <SensitiveText type="address">
                    {[
                      employee.personalInfo.address.street,
                      employee.personalInfo.address.city,
                      employee.personalInfo.address.state,
                      employee.personalInfo.address.zipCode
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </SensitiveText>
                </span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Direct Reports */}
      {reports && reports.length > 0 && (
        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Subordinados Diretos ({reports.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((report) => {
              const reportName = report.personalInfo?.name || 'Nome não informado'
              const reportNameParts = reportName.split(' ').filter(p => p.length > 0)
              const reportInitials = reportNameParts.length >= 2 ?
                `${reportNameParts[0][0]}${reportNameParts[reportNameParts.length - 1][0]}` :
                reportNameParts.length === 1 ?
                `${reportNameParts[0][0]}${reportNameParts[0][1] || 'N'}` :
                'NA'

              return (
                <Link
                  key={report.employeeId}
                  to={`/workforce/employees/${report.employeeId}` as any}
                  className="block"
                >
                  <Card className="p-4 card-hover">
                    <div className="flex items-center gap-3">
                      <EmployeeAvatar
                        src={report.personalInfo?.pictureUrl}
                        name={reportName}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{reportName}</p>
                        <p className="text-xs text-brand-gray truncate">
                          {report.currentEngagement?.roleName || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              )
            })}
          </div>
        </Card>
      )}

      {/* Status Update Modal */}
      {showStatusModal && employee && (
        <UpdateEmployeeStatusModal
          employee={employee}
          onConfirm={async (status, endDate, reason) => {
            await updateStatusMutation.mutateAsync({
              employeeId,
              status,
              endDate,
              reason
            })
            setShowStatusModal(false)
          }}
          onCancel={() => setShowStatusModal(false)}
        />
      )}

      {/* Reassign Manager Modal - Uses employee.managerName/managerEmail (no extra lookup!) */}
      {showReassignModal && employee && (
        <ReassignManagerModal
          employee={employee}
          onConfirm={handleReassignManager}
          onCancel={() => setShowReassignModal(false)}
        />
      )}
    </div>
  )
}

export default withPageProtection(EmployeeDetailsContent, PAGES.WORKFORCE_EMPLOYEES)
